/**
 * **"Here, now"** — the codex's answer to the question a codex is worst at (`TODO.md` §4.2).
 *
 * The world panel is a complete record and an unranked one: everything the story has ever
 * learned, four sections deep, with the place the character is *standing in* filed
 * alphabetically among eight others and the two people in front of them filed among twelve.
 * The player's actual question at the moment they open it is almost always about the present
 * tense, and answering it cost a scroll and a disclosure.
 *
 * So this is pinned above the sections and it is **not** collapsible. That is the point of it:
 * everything else in the panel is something you go and look for, and this is the one thing you
 * are told. Its description is expanded for the same reason — a disclosure on the place you
 * are standing in is a disclosure on the answer.
 *
 * **Everything here is a join, not a derivation.** The place is `character.location.name`
 * looked up in `world.locations`; the factions are that place's own `factions` keys; the
 * people are the NPCs whose `current_location` is that key. All of it is `===` against engine
 * keys the DM declared — never a match against prose, which in three languages would not
 * survive contact with a `ua` story (`../../CLAUDE.md` §7).
 *
 * **The character's location is authoritative and the codex card is optional.** `LocationRef`
 * always carries a display name, so the block can name where you are even in the window
 * before `discover_location` has written a card for it; what a missing card costs is the
 * badge, the description and the factions, and the heading is still correct.
 */
import type { LocationRef, WorldSnapshot } from '@/api/generated';
import { gameTimeLabel } from '@/features/story/dateline';
import { Npc } from '@/features/world/cards';
import { heldBy, toneColor, visitState } from '@/features/world/world';
import { useT } from '@/i18n/context';

interface HereNowProps {
  world: WorldSnapshot;
  /** `character.location` — null only while `GET /stories/{id}/state` has not answered. */
  here: LocationRef | null;
}

export function HereNow({ world, here }: HereNowProps) {
  const t = useT();

  // Without a location there is no "here", and a block that says "here, now" over the clock
  // alone is a heading lying about its contents. The sections below are the whole panel then,
  // which is what the panel was before this block existed.
  if (here === null) return null;

  const card = world.locations.find((place) => place.name === here.name) ?? null;
  const state = card === null ? null : visitState(card.state);
  const factionNames = new Map(
    world.factions.map((faction) => [faction.name, faction.display_name]),
  );
  const present = world.npcs.filter((npc) => npc.current_location === here.name);

  return (
    <section aria-label={t('ui.world.here_now')} className="pb-4">
      <h3
        className="text-fg-faint pb-2 font-mono tracking-wider"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {t('ui.world.here_now')}
      </h3>

      <div className="flex items-baseline gap-2 font-mono" style={{ fontSize: 'var(--text-meta)' }}>
        {/*
          The one place in this panel a world noun is drawn at full strength rather than
          muted. Everything else in the codex is reference; this is where you are.
        */}
        <span className="text-fg truncate">{here.display_name}</span>
        {state !== null ? (
          <span className="ml-auto shrink-0" style={{ color: toneColor(state.tone) }}>
            {t(state.label)}
          </span>
        ) : null}
      </div>

      {/*
        The "now" half, and the only clock inside the codex. It is the same string the
        character panel's foot carries (`dateline.ts`), deliberately — two spellings of the
        game's date is how a player starts distrusting both.
      */}
      <p className="text-fg-faint pt-0.5 font-mono" style={{ fontSize: 'var(--text-meta)' }}>
        {gameTimeLabel(t, world.time)}
      </p>

      {card !== null && (card.description ?? card.short_description) !== null ? (
        <p className="text-fg-muted pt-2" style={{ fontSize: 'var(--text-meta)' }}>
          {card.description ?? card.short_description}
        </p>
      ) : null}

      {card !== null && card.factions.length > 0 ? (
        <p className="text-fg-faint pt-2" style={{ fontSize: 'var(--text-meta)' }}>
          {heldBy(t, card, factionNames)}
        </p>
      ) : null}

      {/*
        The people, drawn with the same card the people section uses so the two cannot drift —
        minus the `last known at`, which here would only repeat the heading two lines up.
        "Nobody else is here" is worth saying rather than omitting: in a game where an NPC can
        be standing behind you, the *absence* of one is information.
      */}
      <p className="text-fg-faint pt-4 font-mono" style={{ fontSize: 'var(--text-meta)' }}>
        {present.length > 0 ? t('ui.world.also_here') : t('ui.world.alone_here')}
      </p>

      {present.length > 0 ? (
        <ul className="flex flex-col pt-1">
          {present.map((npc) => (
            <li key={npc.name}>
              <Npc npc={npc} />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
